import { Bell, Copy, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

import { AppChip } from "@/components/kb/app-mark";
import { GameLine } from "@/components/kb/game-line";
import { LeagueBadge } from "@/components/kb/league-badge";
import { RuleSentence } from "@/components/kb/rule-sentence";
import {
	formatDateTime,
	StatusPill,
	statusFor,
} from "@/components/kb/status-pill";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Condition, type RuleDTO } from "@/lib/api";
import { demoGame } from "@/lib/demo";
import type { Players, Teams } from "@/lib/entities";
import { featuredApps } from "@/lib/featured-apps";
import { cn } from "@/lib/utils";

export function RuleCard({
	rule,
	players,
	teams,
	onEdit,
	onDuplicate,
	onDelete,
	onToggleEnabled,
	busy,
	className,
}: {
	rule: RuleDTO;
	players: Players;
	teams: Teams;
	onEdit: () => void;
	onDuplicate: () => void;
	onDelete: () => void;
	onToggleEnabled: (enabled: boolean) => void;
	busy?: boolean;
	className?: string;
}) {
	const condition = Condition.fromAny(rule.rule);
	const status = statusFor(rule.status, rule.notifyAgainAfter);
	const apps = rule.apps
		.map((id) => featuredApps.find((app) => app.id === id))
		.filter((app) => app !== undefined);
	const showGame = rule.enabled && status.kind === "active";

	return (
		<article
			data-rule-id={rule.id}
			data-testid="rule-card"
			className={cn(
				"flex flex-col gap-4 rounded-xl border bg-card p-4 shadow-xs sm:p-5",
				(!rule.enabled || busy) && "opacity-60",
				className,
			)}
		>
			<div className="flex flex-wrap items-center gap-2">
				<LeagueBadge league="MLB" />
				<span data-testid="rule-status">
					<StatusPill state={status} />
				</span>
				{showGame && (
					<GameLine
						className="hidden sm:inline-flex"
						demo
						game={demoGame(rule.id)}
					/>
				)}
				<div className="ml-auto flex items-center gap-2">
					<Switch
						aria-label={rule.enabled ? "Pause rule" : "Enable rule"}
						data-testid="rule-toggle"
						checked={rule.enabled}
						disabled={busy}
						onCheckedChange={onToggleEnabled}
					/>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								aria-label="Rule actions"
								data-testid="rule-actions"
								disabled={busy}
								size="icon-sm"
								variant="ghost"
							>
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem data-testid="rule-edit" onSelect={onEdit}>
								<Pencil className="size-3.5" />
								Edit
							</DropdownMenuItem>
							<DropdownMenuItem
								data-testid="rule-duplicate"
								onSelect={onDuplicate}
							>
								<Copy className="size-3.5" />
								Duplicate
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								data-testid="rule-delete"
								onSelect={onDelete}
								variant="destructive"
							>
								<Trash2 className="size-3.5" />
								Delete
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			<button
				className="text-left outline-none focus-visible:rounded-md focus-visible:ring-[3px] focus-visible:ring-ring/50"
				data-testid="rule-sentence"
				onClick={onEdit}
				type="button"
			>
				<RuleSentence condition={condition} players={players} teams={teams} />
			</button>

			<div
				className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t pt-3 text-sm text-muted-foreground"
				data-testid="rule-footer"
			>
				<div
					className="flex flex-wrap items-center gap-x-2 gap-y-1"
					data-testid="rule-apps"
				>
					{apps.map((app) => (
						<AppChip app={app} key={app.id} />
					))}
				</div>
				<span className="inline-flex items-center gap-1.5">
					<Bell className="size-3.5" />
					{rule.notifyAgainAfter &&
					new Date(rule.notifyAgainAfter).getTime() > Date.now() ? (
						<span>
							Next alert allowed {formatDateTime(rule.notifyAgainAfter)}
						</span>
					) : (
						<span>Ready for alerts</span>
					)}
				</span>
				{rule.status === "paused" && (
					<span className="ml-auto text-xs">Paused by you</span>
				)}
			</div>
		</article>
	);
}
